/**
 * gst3b.ts — GSTR-3B: input tax credit set-off and net tax payable.
 *
 * GSTR-1 reports outward supplies. GSTR-3B is where the merchant pays: outward
 * tax liability, minus input tax credit on purchases, set off in the order the
 * law mandates, equals the cash actually payable. The GST screen previously
 * showed only the outward liability (3.1(a)), which is the number *before* the
 * one that leaves the bank account.
 *
 * The set-off order is not a preference — it is Section 49/49A + Rule 88A:
 *   • IGST credit is used first (must be fully exhausted before CGST/SGST credit
 *     may be touched), against IGST liability, then CGST, then SGST.
 *   • CGST credit then pays CGST, then IGST. It can never pay SGST.
 *   • SGST credit then pays SGST, then IGST. It can never pay CGST.
 *   • Cess credit pays only cess.
 * Whatever liability survives is paid in cash; whatever credit survives carries
 * forward.
 *
 * All arithmetic is in integer paisa so set-off is exact.
 */
const toPaisa = (r) => Math.round(r * 100);
const toRupees = (p) => Number((p / 100).toFixed(2));
const zero = () => ({ igst: 0, cgst: 0, sgst: 0, cess: 0 });
/**
 * Set input tax credit off against outward liability per Section 49/49A + Rule 88A.
 *
 * Note: `itcAvailable` is this period's credit only. An opening balance in the
 * electronic credit ledger (which we do not track) would reduce cash further —
 * the cash figure here is therefore an upper bound.
 */
export function computeGstr3bSetOff(liabilityR, itcR) {
    // Work in paisa.
    const li = { igst: toPaisa(liabilityR.igst), cgst: toPaisa(liabilityR.cgst), sgst: toPaisa(liabilityR.sgst), cess: toPaisa(liabilityR.cess) };
    const cr = { igst: toPaisa(itcR.igst), cgst: toPaisa(itcR.cgst), sgst: toPaisa(itcR.sgst), cess: toPaisa(itcR.cess) };
    const utilised = { igst: 0, cgst: 0, sgst: 0, cess: 0 };
    /** Pay down `to` liability with `from` credit; record which credit head paid. */
    const apply = (fromHead, toHead) => {
        const amt = Math.min(cr[fromHead], li[toHead]);
        if (amt <= 0)
            return;
        cr[fromHead] -= amt;
        li[toHead] -= amt;
        utilised[fromHead] += amt;
    };
    // 1. IGST credit → IGST, then CGST, then SGST (IGST credit first, in full).
    apply('igst', 'igst');
    apply('igst', 'cgst');
    apply('igst', 'sgst');
    // 2. CGST credit → CGST, then IGST.
    apply('cgst', 'cgst');
    apply('cgst', 'igst');
    // 3. SGST credit → SGST, then IGST.
    apply('sgst', 'sgst');
    apply('sgst', 'igst');
    // 4. Cess credit → cess only.
    apply('cess', 'cess');
    return {
        liability: { ...liabilityR },
        itcAvailable: { ...itcR },
        itcUtilised: { igst: toRupees(utilised.igst), cgst: toRupees(utilised.cgst), sgst: toRupees(utilised.sgst), cess: toRupees(utilised.cess) },
        cashPayable: { igst: toRupees(li.igst), cgst: toRupees(li.cgst), sgst: toRupees(li.sgst), cess: toRupees(li.cess) },
        itcCarriedForward: { igst: toRupees(cr.igst), cgst: toRupees(cr.cgst), sgst: toRupees(cr.sgst), cess: toRupees(cr.cess) },
    };
}
/** Sum of a TaxHeads' components. */
export function totalHeads(h) {
    return Number((h.igst + h.cgst + h.sgst + h.cess).toFixed(2));
}
/** Add two TaxHeads (e.g. opening credit-ledger balance + this period's ITC). */
export function addHeads(a, b) {
    const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
    return {
        igst: round2(a.igst + b.igst),
        cgst: round2(a.cgst + b.cgst),
        sgst: round2(a.sgst + b.sgst),
        cess: round2(a.cess + b.cess),
    };
}
export { zero as zeroHeads };
